import { Mui, MuiIcons } from "@osu/react-ui";
import { controlOptions, sortOptions } from "../util/reducer";
import { forwardRef, useEffect, useRef, useState } from "react";
import DeleteAction from "./DeleteAction";

const sortIconSx = (theme) => {
  return {
    [`&:focus`]: {
      border: `2px solid ${theme.palette.action.active}`
    }
  }
}

const SortAction = forwardRef(({ type = "", onClick, id, ...rest }, ref) => {
  if (type !== sortOptions.up && type !== sortOptions.down) {
    return false;
  }
  const Icon =
    type === sortOptions.up ? MuiIcons.ArrowUpward : MuiIcons.ArrowDownward;
  return (
    <Mui.IconButton {...rest} id={id} ref={ref} size="small" aria-label={`move ${type}`} onClick={onClick} sx={[sortIconSx, {
      marginRight: .5
    }]}>
      <Icon />
    </Mui.IconButton>
  );
});

function isEven(num) {
  return num % 2 === 0;
}

const styles = (theme, index, rowIsActive) => {
  const even = isEven(index);
  const contrastFn = theme.palette.mode === "dark" ? Mui.lighten : Mui.darken;
  let bg = even
    ? theme.palette.background.paper
    : contrastFn(theme.palette.background.paper, 0.05);
  let border = ''
  if(rowIsActive) {
    border = `1px solid ${contrastFn(bg, .1)}`
  }
  return {
    paddingY: 1,
    backgroundColor: bg,
    color: theme.palette.getContrastText(bg),
    border
  };
};

const createListItemStyles = (theme, isActive) => {
  if(isActive) {
    return {
      [`&.${Mui.listItemClasses.root}`]: {
        scale: '101%',
        boxShadow: theme.shadows[5],
        zIndex: 99,
        borderRadius: '5px'
      },
      [`& *.${Mui.typographyClasses.root}`]: {
        fontWeight: theme.typography.fontWeightBold
      }
    }
  }
}

const compareElementId = (activeElementId, id) => {
  if(activeElementId && id) {
    return !!activeElementId.startsWith(id)
  }
  return false
}

const ListItem = ({ link, index, reducer, deletable, lastAction, arrayLength, isSuccess, isLoading }) => {
  const downRef = useRef()
  const upRef = useRef()
  const removeRef = useRef()
  const isItemAMatchToPriorAction = lastAction?.link?.link && (link?.link === lastAction?.link?.link)
  const id = (link?.identifier) || encodeURIComponent(link?.title)
  const deleteButtonDisabled = !deletable
  const activeElementId = document?.activeElement?.id
  const [rowIsActive, setRowIsActive] = useState(compareElementId(activeElementId, id))
  const isVerySmallDevice = Mui.useMediaQuery('(max-width:480px)')
  const onClick = (type) =>
    reducer({
      link,
      type,
  });

  useEffect(() => {
    const activeAlementIsBody = document.activeElement.tagName === 'BODY'
    const focusFirstChild = !isLoading && isSuccess && activeAlementIsBody
    if(focusFirstChild  && downRef?.current?.focus && index === 0) {
      downRef.current.focus()
    }
  }, [index, isLoading, isSuccess])

  useEffect(() => {
      /**Focus management effect */
      const lastActionType = lastAction?.type ?? ""

      switch (lastActionType) {
        case sortOptions.down:
          if(isItemAMatchToPriorAction) downRef.current.focus()
            break;
        case sortOptions.up:
          if(isItemAMatchToPriorAction) upRef.current.focus()
          break;
        case controlOptions.remove:
          if(!isNaN(lastAction?.indexOf)) {
            const deletedLastItem = (lastAction.indexOf >= arrayLength)
            const onTheRowAfterThePreviouslyDeletedItem = lastAction.indexOf === index
            if(onTheRowAfterThePreviouslyDeletedItem) {
              downRef.current.focus()
            } else if(deletedLastItem) {
              const isLastItemInList = index === (arrayLength - 1)
              if(isLastItemInList) {
                if(removeRef?.current) {
                  if(deleteButtonDisabled) {
                    (upRef.current || downRef.current).focus()
                  } else {
                    removeRef.current.focus()
                  }
                } else {
                  downRef.current.focus()
                }
              }
            }
          }
          break;
        case controlOptions.reset:
          if(index === 0) {
            downRef.current.focus()
          }
          break;
        default:
          break;
      }
  }, [arrayLength, deleteButtonDisabled, index, isItemAMatchToPriorAction, lastAction])

  const removeFn = () => {
    reducer({
      link,
      type: controlOptions.remove,
    });
  }

  const onClickableListItemChildBlur = (event) => {
    const isNextItemInList = compareElementId(event?.relatedTarget?.id, id)
    setRowIsActive(isNextItemInList)
  }

  const onClickableListItemChildFocus = (event) => {
    const isNextItemInList = compareElementId(event?.target?.id, id)
    setRowIsActive(isNextItemInList)
  }

  return (
    <Mui.ListItem
      sx={[
        (theme) => styles(theme, index, rowIsActive),
        (theme) => createListItemStyles(theme, rowIsActive)
      ]}
      secondaryAction={
         (
          <DeleteAction 
            ref={removeRef}
            sx={sortIconSx}
            disabled={deleteButtonDisabled}
            aria-disabled={deleteButtonDisabled}
            onClick={removeFn}
            onKeyUp={(event) => {
              if(event?.key === 'Backspace') {
                removeFn()
              }
            }}
            onFocus={onClickableListItemChildFocus}
            id={id + '-' + controlOptions.remove}
            onBlur={onClickableListItemChildBlur}
          />
        )
      }
    >
      <SortAction
        onClick={() => onClick(sortOptions.down)}
        ref={downRef}
        type={sortOptions.down}
        id={id + '-' + sortOptions.down}
        onBlur={onClickableListItemChildBlur}
        onFocus={onClickableListItemChildFocus}
      />
      {!isVerySmallDevice && <SortAction
        ref={upRef}
        onClick={() => onClick(sortOptions.up)}
        type={sortOptions.up}
        id={id + '-' + sortOptions.up}
        onBlur={onClickableListItemChildBlur}
        onFocus={onClickableListItemChildFocus}
      />}
      <Mui.ListItemText
        className="margin-x-1"
      >
        {link?.title}
      </Mui.ListItemText>
    </Mui.ListItem>
  );
};

export default ListItem;
